import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import {
  IonHeader,
  IonToolbar,
  IonButton,
  IonButtons,
  IonIcon,
  IonTabs,
  IonTabBar,
  IonTabButton,
  IonBadge,
  IonText,
  IonLabel,
  IonImg,
  NavController,
  IonSkeletonText,
  MenuController
} from '@ionic/angular/standalone';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BusinessService } from 'src/app/core/services/business/business.service';
import { Subscription } from 'rxjs';
import { NotificationsService } from 'src/app/core/services/notifications/notifications.service';
import { TabService } from 'src/app/core/services/navigation/tab.service';
import { StateType } from 'src/app/core/models/StateType';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { Business } from 'src/app/core/models/Business';
import { addIcons } from 'ionicons';
import { chevronBackOutline, notifications, notificationsOutline, informationCircle, newspaper, cart, swapHorizontalOutline, logOutOutline, heart, heartOutline, searchOutline, timeOutline, calendarOutline } from 'ionicons/icons';
import { environment } from 'src/environments/environment';
import { isDesktopViewport } from 'src/app/core/utilities/viewport';

const BUSINESS_TABS: string[] = [
  '/business/business-info',
  '/business/shopify-management',
  '/business/donate',
  '/business/social-content',
  '/business/items',
];

/** Tab names derived from BUSINESS_TABS so they stay in sync (for handleTabChange / isMainPage). */
const businessTabs: string[] = BUSINESS_TABS.map(path => path.split('/').pop() as string);

@Component({
    selector: 'app-business-role-tabs',
    templateUrl: './business-role-tabs.component.html',
    styleUrls: ['./business-role-tabs.component.scss'],
    imports: [
        IonHeader,
        IonToolbar,
        IonButton,
        IonButtons,
        IonIcon,
        IonTabs,
        IonTabBar,
        IonTabButton,
        IonBadge,
        IonText,
        IonLabel,
        IonImg,
        IonSkeletonText
    ]
})
export class BusinessRoleTabsComponent implements OnInit, OnDestroy {
  private readonly subscriptions: Subscription = new Subscription();
  private menuController: MenuController = inject(MenuController);
  authService: AuthService = inject(AuthService);
  appName: string = environment.appName;

  currentTab: string = '';
  /** Sidebar highlight: derived from the current URL so it follows back/forward and deep links. */
  activeSection: string = '';
  isMainPage: boolean = true;
  notificationCount: number = 0;
  business: Business | null = null;
  imageLoaded: boolean = false;
  avatarSrc: string | null = null;
  pageTitle: string = '';
  private isNavigating: boolean = false;

  get pageTitleClass(): string {
    return this.pageTitle && this.pageTitle.length > 14 ? 'page-title page-title-small' : 'page-title';
  }

  constructor(
    private navController: NavController,
    private router: Router,
    private businessService: BusinessService,
    private notificationsService: NotificationsService,
    private tabService: TabService,
    private route: ActivatedRoute
  ) {
    addIcons({ chevronBackOutline, notifications, notificationsOutline, informationCircle, newspaper, cart, swapHorizontalOutline, logOutOutline, heart, heartOutline, searchOutline, timeOutline, calendarOutline });

    const currentUrl: string = this.router.url;
    if (currentUrl.startsWith('/business/')) {
      const tabName: string | undefined = currentUrl.split('/')[2]?.split('?')[0];
      if (tabName && businessTabs.includes(tabName)) {
        this.currentTab = tabName;
        this.tabService.setLastTab('business', tabName);
      }
    }

    if (!this.currentTab) this.currentTab = this.tabService.getLastTab('business');
    this.activeSection = this.tabService.findTabForUrl('business', this.router.url, this.currentTab);

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const url: string = event.urlAfterRedirects;
        if (!url.startsWith('/business')) return;
        this.isMainPage = !!BUSINESS_TABS.find(page => page === url);
        
        const state: StateType = this.router.getCurrentNavigation()?.extras.state || {};
        const sourceTab: string | undefined = state['sourceTab'];
        
        if (this.isMainPage) {
          const tabName: string | undefined = url.split('/').pop();
          if (tabName && businessTabs.includes(tabName)) {
            this.currentTab = tabName;
            this.tabService.setLastTab('business', tabName);
          }
        } else if (sourceTab) {
          this.currentTab = sourceTab;
          this.tabService.setLastTab('business', sourceTab);
        }
        
        this.activeSection = this.tabService.findTabForUrl('business', url, this.currentTab);

        let currentRoute: ActivatedRoute = this.route.root;
        while (currentRoute.firstChild) {
          currentRoute = currentRoute.firstChild;
        }
        this.pageTitle = currentRoute.snapshot.data['title'] || '';
        this.tabService.trackNavigation(
          'business',
          this.currentTab,
          event.urlAfterRedirects,
          state,
        );
      }
    });
  }

  ngOnInit(): void {
    this.subscribeToBusinessChanges();
    this.subscribeToNotifications();
  }

  private subscribeToBusinessChanges(): void {
    const businessSubscription: Subscription = this.businessService.business$.subscribe({
      next: (business: Business | null) => {
        // SUN-1279: business$ re-emits a fresh object on every refresh. Only reset the load
        // state when the URL changes; an unchanged ion-img src never fires ionImgDidLoad again,
        // which left the avatar hidden.
        const avatarUrl: string | null = business?.avatarUrl || null;
        if (avatarUrl !== this.avatarSrc) {
          this.avatarSrc = avatarUrl;
          this.imageLoaded = false;
        }
        this.business = business;
        if (business?.id) {
          this.notificationsService.loadUnreadCountByBusinessId(business.id);
        }
      }
    });
    this.subscriptions.add(businessSubscription);
  }

  private subscribeToNotifications(): void {
    const notificationSubscription: Subscription = this.notificationsService.businessNotificationCount$.subscribe({
      next: (count: number | null) => {
        this.notificationCount = count ?? 0;
      }
    });
    this.subscriptions.add(notificationSubscription);
  }

  onImageLoad(): void {
    this.imageLoaded = true;
  }

  onImageError(): void {
    // A broken avatar URL falls back to the default icon instead of an empty slot.
    if (this.avatarSrc) {
      this.avatarSrc = null;
      this.imageLoaded = false;
    }
  }

  openMenu(): void {
    this.menuController.open('business-menu');
  }

  goToNotifications(): void {
    this.navController.navigateForward(['/business/notifications']);
  }

  goToDiscover(): void {
    this.navController.navigateForward(['/business/discover']);
  }

  goToDonationHistory(): void {
    this.navController.navigateForward(['/business/donation-history']);
  }

  goToRecurringDonations(): void {
    this.navController.navigateForward(['/business/recurring-donations']);
  }

  back(): void {
    if (!this.currentTab) this.currentTab = this.tabService.getLastTab('business');
    const backTabPage: { url: string; state: StateType } | null = this.tabService.navigateBack(
      'business',
      this.currentTab,
    );
    if (backTabPage) {
      this.navController.navigateBack(backTabPage.url, { state: backTabPage.state || {} });
    } else {
      this.navController.navigateRoot(`/business/${this.currentTab}`);
    }
  }

  navigateToTab(tab: string): void {
    if (this.isNavigating) return;

    this.isNavigating = true;

    if (isDesktopViewport()) {
      // Desktop sidebar: always open the section root and drop that tab's stack. Restoring the
      // last deep page (the mobile tab-bar behavior below) made sidebar links feel broken.
      this.currentTab = tab;
      this.activeSection = tab;
      this.tabService.setLastTab('business', tab);
      this.tabService.resetTabHistory('business', tab);
      this.navController.navigateRoot(`/business/${tab}`)
        .finally(() => {
          this.isNavigating = false;
        });
      return;
    }

    if (tab === this.currentTab) {
      this.tabService.resetTabHistory('business', tab);
      this.navController.navigateRoot(`/business/${tab}`)
        .finally(() => {
          this.isNavigating = false;
        });
    } else {
      this.currentTab = tab;
      this.tabService.setLastTab('business', tab);
      const lastTabPage: { url: string; state: StateType } | null = this.tabService.getLastTabPage(
        'business',
        tab,
      );
      if (lastTabPage) {
        this.navController.navigateRoot(lastTabPage.url, { state: lastTabPage.state || {} })
          .finally(() => {
            this.isNavigating = false;
          });
      } else {
        this.navController.navigateRoot(`/business/${tab}`)
          .finally(() => {
            this.isNavigating = false;
          });
      }
    }
  }

  /**
   * Bookkeeping only — `ion-tabs` has already started the navigation (it restores the tab's last
   * page or root itself). Navigating here too re-ran the same URL with `onSameUrlNavigation:
   * 'reload'`, rebuilding the page on every tab tap. See UserRoleTabsComponent.handleTabChange.
   */
  handleTabChange(event: { tab: string }): void {
    const page: string = event.tab;
    if (!businessTabs.includes(page)) {
      if (!this.currentTab) this.currentTab = this.tabService.getLastTab('business');
      return;
    }
    if (page !== this.currentTab) {
      this.currentTab = page;
      this.tabService.setLastTab('business', page);
    }
  }

  sidebarSwitchToUser(): void {
    const lastPage: { url: string; state: StateType } | null = this.tabService.getLastPage('user');
    this.navController.navigateRoot(lastPage?.url || '/main/home', {
      state: lastPage?.state || {},
    });
  }

  async sidebarSignOut(): Promise<void> {
    await this.authService.logout();
    this.navController.navigateRoot('/welcome');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
